import React from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./pagination.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select.tsx";

interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  startItem: number;
  endItem: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  onFirstPage: () => void;
  onLastPage: () => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  startItem,
  endItem,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  onItemsPerPageChange,
  onFirstPage: _onFirstPage,
  onLastPage: _onLastPage,
  onNextPage,
  onPreviousPage,
}) => {
  const getPageNumbers = () => {
    const pages = [];
    const isTinyScreen = typeof window !== 'undefined' && window.innerWidth < 380;
    if (totalPages <= 1) return [1];
    if (isTinyScreen) {
      if (currentPage < totalPages) {
        pages.push(currentPage);
        pages.push(currentPage + 1);
      } else if (currentPage > 1) {
        pages.push(currentPage - 1);
        pages.push(currentPage);
      } else {
        pages.push(currentPage);
      }
    } else {
      if (currentPage > 1) pages.push(currentPage - 1);
      pages.push(currentPage);
      if (currentPage < totalPages) pages.push(currentPage + 1);
    }
    return pages;
  };
  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 py-2 md:px-6 md:py-4 border-t rounded-b-lg bg-background gap-4 md:gap-0">
      <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-muted-foreground">
        <div className="flex items-center space-x-2">
          <span className="whitespace-nowrap">Rows per page</span>
          <Select
            value={itemsPerPage.toString()}
            onValueChange={(value) => onItemsPerPageChange(parseInt(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 10, 20, 50, 100].map((value) => (
                <SelectItem key={value} value={value.toString()}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="whitespace-nowrap">
          {startItem}-{endItem} of {totalItems}
        </div>
      </div>

      <div className="w-full md:w-auto overflow-x-auto">
        <Pagination>
          <PaginationContent className="flex-nowrap">
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  onPreviousPage();
                }}
                className={
                  !hasPreviousPage
                    ? "pointer-events-none opacity-50"
                    : "cursor-pointer"
                }
                size='default'
              />
            </PaginationItem>
            {getPageNumbers().map((pageNum) => (
              <PaginationItem key={'page-' + pageNum}>
                <PaginationLink
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(pageNum);
                  }}
                  isActive={pageNum === currentPage}
                  className="cursor-pointer"
                  size='default'
                >
                  {pageNum}
                </PaginationLink>
              </PaginationItem>
            ))}

            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  onNextPage();
                }}
                className={!hasNextPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
                size='default'
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};

export default TablePagination;